import type { FileImportConfig } from './FileImportSection'
import { TransactionImportDetailsModal } from './TransactionImportDetailsModal'
import { ProjectionImportDetailsModal } from './ProjectionImportDetailsModal'
// Keys come from the dependency-free module on purpose: importing them from the
// hook modules pulled the supabase client and the mock banking data into every
// consumer of this config (DCP-298 review). See `@/lib/query-keys`.
import { BANKING_KEYS, PROJECTION_KEYS, CASH_POSITION_SUMMARIES_KEY } from '@/lib/query-keys'

/** Configuration for transaction file imports */
export const transactionImportConfig: FileImportConfig = {
  importType: 'transaction',
  title: 'Upload Transactions File',
  description:
    'Upload a CSV or Excel file with your transactions. Required columns: transaction_date, amount, description, facility, bank_account_name, bank_account_number. Each row must have values in these columns. Other columns are optional.',
  requiredHeaders: [
    'transaction_date',
    'amount',
    'description',
    'facility',
    'bank_account_name',
    'bank_account_number',
  ],
  optionalHeaders: ['category', 'vendor', 'currency', 'type'],
  templateSheetName: 'Transactions',
  templateFileName: 'dcp_transactions_import_template.xlsx',
  // Imported transactions feed the manual-transaction lists/counts and the
  // banking KPI tiles.
  //
  // They also feed LOC Usage and the facility-category chart, which is less
  // obvious: `process-file-import` inserts into the tenant `transactions` table
  // with `source: 'file_import'` — the very table `get_loc_availability` /
  // `get_loc_transactions` sum for `category = 'LINE OF CREDIT'` (DCP-290) and
  // the chart's `facilityCategoryChart` RPC reads. So a file with LOC rows used
  // to leave LOC Usage and Remaining Credit Availability on pre-import figures
  // for the full 5min `staleTime` — the same class of defect DCP-298 fixed for
  // projections. Mirrors what `useUpdateManualTransaction` invalidates for a
  // single-row edit; an import is the same write in bulk.
  //
  // NOT covered here, deliberately stated rather than implied (PR #183 review):
  // the Banking Dashboard's transactions table reads `useBankTransactions`
  // (`@/hooks/usePlaid`) under `['plaid','transactions']`, which nothing in the
  // import path invalidates. The hooks that do — `useSyncTransactions` /
  // `useSyncAllTransactions`, `invalidateFacilityScopedCaches`, the LedgerSync
  // sync — all fire from a Sync button or a facility mutation, never from a file
  // upload, so that table keeps pre-import rows for the 5min `staleTime`.
  //
  // Left as-is because it is NOT this ticket's regression and not import-specific:
  // `useUpdateManualTransaction` has the identical gap for a single-row edit, and
  // the two should be fixed together (add `['plaid','transactions']` to both) so
  // the bulk and single-row paths keep mirroring each other. An earlier version
  // of this comment read as though the sync hooks covered the import; they do
  // not.
  invalidateKeys: [
    BANKING_KEYS.allManualTransactions,
    BANKING_KEYS.allManualTransactionCounts,
    BANKING_KEYS.allKpi,
    BANKING_KEYS.allLocAvailability,
    BANKING_KEYS.allLocTransactions,
    BANKING_KEYS.allFacilityCategories,
  ],
  columnFormatHelp: (
    <>
      <strong>transaction_date</strong> — YYYY-MM-DD format.{' '}
      <strong>amount</strong> — decimal number (negative for outflows).{' '}
      <strong>facility</strong> — must match existing facility name.{' '}
      <strong>bank_account_name</strong>, <strong>bank_account_number</strong> — must match existing
      bank account.
    </>
  ),
  DetailsModal: TransactionImportDetailsModal,
}

/** Configuration for projection file imports */
export const projectionImportConfig: FileImportConfig = {
  importType: 'projection',
  title: 'Upload Projections File',
  description:
    'Upload a CSV or Excel file with your projections. Required columns: facility, category, date, description, amount. Each row must have values in these columns. Notes column is optional.',
  requiredHeaders: ['facility', 'category', 'date', 'description', 'amount'],
  optionalHeaders: ['notes'],
  templateSheetName: 'Projections',
  templateFileName: 'dcp_projections_import_template.xlsx',
  // Imported projections feed the Projections table and every Cash Position
  // summary. Mirrors what the manual projection mutations in
  // `useProjections.ts` invalidate after a create/update/delete.
  invalidateKeys: [PROJECTION_KEYS.all, CASH_POSITION_SUMMARIES_KEY],
  columnFormatHelp: (
    <>
      <strong>facility</strong> — must match existing facility name.{' '}
      <strong>category</strong> — must match existing category name.{' '}
      <strong>date</strong> — YYYY-MM-DD format.{' '}
      <strong>description</strong> — free text description.{' '}
      <strong>amount</strong> — decimal number (positive for inflows, negative for outflows).
    </>
  ),
  DetailsModal: ProjectionImportDetailsModal,
}
